import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Image,
  Center,
  Flex,
  Container,
  Heading,
  Badge,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiX, FiPlus, FiVideo, FiImage, FiFileText } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { GENRES } from '../constants/genres';
import { CONTENT_RATINGS } from '../constants/contentRatings';
import { getContentById, updateContent } from '../services/contentService';

const EditMovie: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [content, setContent] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'movie',
    tags: '',
    visibility: 'public',
    rating: '',
    language: 'English',
    genres: [] as string[],
    cast: '',
    director: '',
    writer: '',
    contentAdvisory: '',
    posterFile: null as File | null,
    trailerFile: null as File | null,
    videoFile: null as File | null,
    subtitleFiles: [] as Array<{ file: File; language: string; id: string }>,
    posterPreview: '',
    trailerPreview: '',
    videoPreview: ''
  });

  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    if (id) {
      loadMovieData();
    }
  }, [id]);

  const loadMovieData = async () => {
    try {
      setLoading(true);
      const movieData = await getContentById(id!);
      setContent(movieData);
      
      if (movieData) {
        // Find existing video URL from assets
        let existingVideoUrl = '';
        if (movieData.assets) {
          // Check for common video keys
          if (movieData.assets['videoFile']) {
            existingVideoUrl = movieData.assets['videoFile'] as string;
          } else if (movieData.assets['video']) {
            existingVideoUrl = movieData.assets['video'] as string;
          } else if (movieData.assets['videoUrl']) {
            existingVideoUrl = movieData.assets['videoUrl'] as string;
          } else {
            // Look for any video file in assets
            for (const [key, value] of Object.entries(movieData.assets)) {
              if (typeof value === 'string' && 
                  (value.includes('firebasestorage') || value.includes('http')) &&
                  (key.toLowerCase().includes('video') || 
                   value.toLowerCase().includes('.mp4') || 
                   value.toLowerCase().includes('.mov') ||
                   value.toLowerCase().includes('.avi'))) {
                existingVideoUrl = value;
                break;
              }
            }
          }
        }

        setFormData({
          title: movieData.title || '',
          description: movieData.description || '',
          category: 'movie',
          tags: Array.isArray(movieData.tags) ? movieData.tags.join(', ') : (movieData.tags || ''),
          visibility: 'public', // Default since visibility doesn't exist in ContentItem
          rating: String(movieData.rating || ''),
          language: movieData.language || 'English',
          genres: movieData.genres || [],
          cast: movieData.cast ? movieData.cast.join(', ') : '',
          director: movieData.director ? movieData.director.join(', ') : '',
          writer: movieData.writer ? movieData.writer.join(', ') : '',
          contentAdvisory: '', // Default since contentAdvisory doesn't exist in ContentItem
          posterFile: null,
          trailerFile: null,
          videoFile: null,
          subtitleFiles: movieData.assets && movieData.assets['subtitles'] ? 
            (Array.isArray(movieData.assets['subtitles']) ? 
              movieData.assets['subtitles'].map((url: string, index: number) => ({
                file: new File([], `subtitle_${index}.srt`), // Dummy file for display
                language: 'English',
                id: `existing_${index}`,
                url: url // Store the actual URL
              })) : []) : [],
          posterPreview: movieData.poster || movieData.thumbnail || '',
          trailerPreview: movieData.trailer || '',
          videoPreview: existingVideoUrl
        });
      }
    } catch (error) {
      console.error('Error loading movie data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load movie data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenreToggle = (genre: string) => {
    setFormData(prev => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter(g => g !== genre)
        : [...prev.genres, genre].slice(0, 3)
    }));
  };

  const handlePosterSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      posterFile: file,
      posterPreview: URL.createObjectURL(file)
    }));
  };

  const handlePosterRemove = () => {
    if (formData.posterPreview && formData.posterFile) {
      URL.revokeObjectURL(formData.posterPreview);
    }
    setFormData(prev => ({
      ...prev,
      posterFile: null,
      posterPreview: content?.poster || content?.thumbnail || ''
    }));
  };

  const handleTrailerSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      trailerFile: file,
      trailerPreview: URL.createObjectURL(file)
    }));
  };

  const handleTrailerRemove = () => {
    if (formData.trailerPreview && formData.trailerFile) {
      URL.revokeObjectURL(formData.trailerPreview);
    }
    setFormData(prev => ({
      ...prev,
      trailerFile: null,
      trailerPreview: content?.trailer || ''
    }));
  };

  const handleVideoSelect = (file: File) => {
    setFormData(prev => ({
      ...prev,
      videoFile: file,
      videoPreview: URL.createObjectURL(file)
    }));
  };

  const handleVideoRemove = () => {
    if (formData.videoPreview && formData.videoFile) {
      URL.revokeObjectURL(formData.videoPreview);
    }
    // Reset to existing video URL if available, otherwise empty
    setFormData(prev => ({
      ...prev,
      videoFile: null,
      videoPreview: content?.assets?.videoFile || content?.assets?.video || content?.assets?.videoUrl || ''
    }));
  };

  const handleSubtitleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newSubtitles = files.map(file => ({
        file,
        language: 'English',
        id: Math.random().toString(36).substr(2, 9)
      }));
      setFormData(prev => ({
        ...prev,
        subtitleFiles: [...prev.subtitleFiles, ...newSubtitles]
      }));
    }
  };

  const handleSubtitleRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      subtitleFiles: prev.subtitleFiles.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!user || !id) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const updateData = {
        title: formData.title,
        description: formData.description,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        isPublic: formData.visibility === 'public',
        rating: Number(formData.rating) || 0,
        language: formData.language,
        genres: formData.genres,
        cast: formData.cast.split(',').map(name => name.trim()).filter(name => name),
        director: formData.director.split(',').map(name => name.trim()).filter(name => name),
        writer: formData.writer.split(',').map(name => name.trim()).filter(name => name),
      };

      const assetUrls: Record<string, string | string[]> = {};

      // Upload poster if changed
      if (formData.posterFile) {
        const { uploadFileDirect } = await import('../services/uploadService');
        const posterUrl = await uploadFileDirect(`movies/posters/${user.uid}`, formData.posterFile);
        assetUrls.poster = posterUrl;
        setUploadProgress(25);
      }

      // Upload trailer if changed
      if (formData.trailerFile) {
        const { uploadFileDirect } = await import('../services/uploadService');
        const trailerUrl = await uploadFileDirect(`movies/trailers/${user.uid}`, formData.trailerFile);
        assetUrls.trailer = trailerUrl;
        setUploadProgress(50);
      }

      // Upload video if changed
      if (formData.videoFile) {
        const { uploadFileDirect } = await import('../services/uploadService');
        const videoUrl = await uploadFileDirect(`movies/videos/${user.uid}`, formData.videoFile);
        assetUrls.videoFile = videoUrl;
        setUploadProgress(75);
      }

      // Handle subtitles (both existing and new)
      if (formData.subtitleFiles.length > 0) {
        const subtitleUrls: string[] = [];
        const { uploadFileDirect } = await import('../services/uploadService');
        
        for (const subtitle of formData.subtitleFiles) {
          if ((subtitle as any).url) {
            // Existing subtitle - keep the URL
            subtitleUrls.push((subtitle as any).url);
          } else {
            // New subtitle - upload the file
            const subtitleUrl = await uploadFileDirect(`movies/subtitles/${user.uid}`, subtitle.file);
            subtitleUrls.push(subtitleUrl);
          }
        }
        assetUrls.subtitles = subtitleUrls;
        setUploadProgress(90);
      }

      await updateContent(id, updateData, assetUrls);
      setUploadProgress(100);

      toast({
        title: 'Movie updated successfully!',
        description: 'Your movie has been updated.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate(`/content/${id}`);
    } catch (error) {
      console.error('Error updating movie:', error);
      toast({
        title: 'Update failed',
        description: 'There was an error updating your movie. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text>Loading movie data...</Text>
        </VStack>
      </Center>
    );
  }

  if (!content) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text>Movie not found</Text>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
        <Container maxW="1200px">
          <HStack gap={4} align="center">
            <IconButton
              aria-label="Go back"
              icon={<IconWrapper icon={FiArrowLeft} size={20} />}
              variant="ghost"
              onClick={() => navigate(`/content/${id}`)}
              size="sm"
              bg="purple.100"
              _hover={{ bg: 'purple.200' }}
            />
            <VStack align="start" gap={1}>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Edit Movie
              </Text>
              <Text color={labelColor} fontSize="sm">
                Update your movie details
              </Text>
            </VStack>
          </HStack>
        </Container>
      </Box>

      {/* Progress Bar */}
      {uploading && (
        <Box w="100%" bg="gray.200" borderRadius="full" h="4px">
          <Box
            bg="purple.500"
            h="100%"
            borderRadius="full"
            transition="width 0.3s"
            w={`${uploadProgress}%`}
          />
        </Box>
      )}

      {/* Main Content */}
      <Container maxW="1200px" py={8}>
        <VStack gap={8} align="stretch">
          {/* Movie Information */}
          <VStack gap={6} align="stretch">
            {/* Title */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Movie Title *
              </FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter movie title"
                bg={inputBg}
                borderColor={borderColor}
                size="lg"
              />
            </FormControl>

            {/* Description */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Description *
              </FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter movie description"
                bg={inputBg}
                borderColor={borderColor}
                rows={4}
                size="lg"
              />
            </FormControl>

            {/* Genres */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Genres * (Select up to 3)
              </FormLabel>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} gap={2}>
                {GENRES.map(genre => (
                  <Button
                    key={genre}
                    size="sm"
                    variant={formData.genres.includes(genre) ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => handleGenreToggle(genre)}
                    isDisabled={!formData.genres.includes(genre) && formData.genres.length >= 3}
                    fontSize={{ base: "xs", sm: "sm" }}
                    px={{ base: 2, sm: 3 }}
                  >
                    {genre}
                  </Button>
                ))}
              </SimpleGrid>
              {formData.genres.length > 0 && (
                <HStack gap={2} mt={2}>
                  <Text fontSize="sm" color={labelColor}>Selected:</Text>
                  {formData.genres.map(genre => (
                    <Tag key={genre} colorScheme="purple" size="sm">
                      <TagLabel>{genre}</TagLabel>
                      <TagCloseButton onClick={() => handleGenreToggle(genre)} />
                    </Tag>
                  ))}
                </HStack>
              )}
            </FormControl>

            {/* Content Rating */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Content Rating
              </FormLabel>
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={2}>
                {CONTENT_RATINGS.map(rating => (
                  <Button
                    key={rating.value}
                    size="sm"
                    variant={formData.rating === rating.value ? 'solid' : 'outline'}
                    colorScheme="purple"
                    onClick={() => handleInputChange('rating', rating.value)}
                    textAlign="left"
                    justifyContent="flex-start"
                    h="auto"
                    minH="60px"
                    p={3}
                    whiteSpace="normal"
                    wordBreak="break-word"
                    w="full"
                  >
                    <VStack align="start" gap={0.5} w="full" spacing={0}>
                      <Text 
                        fontSize="sm" 
                        fontWeight="bold" 
                        noOfLines={1} 
                        w="full"
                        color={formData.rating === rating.value ? 'white' : textColor}
                      >
                        {rating.value}
                      </Text>
                      <Text 
                        fontSize="xs" 
                        noOfLines={3} 
                        w="full" 
                        lineHeight="1.2"
                        color={formData.rating === rating.value ? 'whiteAlpha.800' : 'gray.500'}
                      >
                        {rating.description}
                      </Text>
                    </VStack>
                  </Button>
                ))}
              </SimpleGrid>
            </FormControl>

            {/* Cast & Crew */}
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Cast
                </FormLabel>
                <Input
                  value={formData.cast}
                  onChange={(e) => handleInputChange('cast', e.target.value)}
                  placeholder="Actor 1, Actor 2, Actor 3"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Director
                </FormLabel>
                <Input
                  value={formData.director}
                  onChange={(e) => handleInputChange('director', e.target.value)}
                  placeholder="Director 1, Director 2"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>
            </SimpleGrid>

            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Writer
              </FormLabel>
              <Input
                value={formData.writer}
                onChange={(e) => handleInputChange('writer', e.target.value)}
                placeholder="Writer 1, Writer 2"
                bg={inputBg}
                borderColor={borderColor}
              />
            </FormControl>

            {/* Poster Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Movie Poster
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.posterFile ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={8}
                textAlign="center"
                bg={formData.posterFile ? 'purple.50' : inputBg}
                _dark={{ bg: formData.posterFile ? 'purple.900' : 'gray.700' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('poster-upload')?.click()}
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.posterPreview ? (
                  <VStack gap={3}>
                    <Image
                      src={formData.posterPreview}
                      alt="Poster preview"
                      maxH="150px"
                      borderRadius="md"
                    />
                    <Text color="purple.600" fontWeight="medium">
                      {formData.posterFile ? formData.posterFile.name : 'Current poster'}
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePosterRemove();
                      }}
                    >
                      Remove
                    </Button>
                  </VStack>
                ) : (
                  <VStack gap={2}>
                    <IconWrapper icon={FiImage} size={24} color="gray.400" />
                    <Text color="gray.500">Click to upload poster</Text>
                  </VStack>
                )}
                <Input
                  id="poster-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handlePosterSelect(file);
                  }}
                  display="none"
                />
              </Box>
            </FormControl>

            {/* Trailer Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Trailer (Optional)
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.trailerFile ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={8}
                textAlign="center"
                bg={formData.trailerFile ? 'purple.50' : inputBg}
                _dark={{ bg: formData.trailerFile ? 'purple.900' : 'gray.700' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('trailer-upload')?.click()}
                minH="120px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.trailerPreview ? (
                  <VStack gap={3}>
                    <video
                      src={formData.trailerPreview}
                      style={{ maxHeight: '100px', borderRadius: '8px' }}
                      controls
                    />
                    <Text color="purple.600" fontWeight="medium">
                      {formData.trailerFile ? formData.trailerFile.name : 'Current trailer'}
                    </Text>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTrailerRemove();
                      }}
                    >
                      Remove
                    </Button>
                  </VStack>
                ) : (
                  <VStack gap={2}>
                    <IconWrapper icon={FiVideo} size={24} color="gray.400" />
                    <Text color="gray.500">Click to upload trailer</Text>
                  </VStack>
                )}
                <Input
                  id="trailer-upload"
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleTrailerSelect(file);
                  }}
                  display="none"
                />
              </Box>
            </FormControl>

            {/* Video Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Movie Video
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.videoFile ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={8}
                textAlign="center"
                bg={formData.videoFile ? 'purple.50' : inputBg}
                _dark={{ bg: formData.videoFile ? 'purple.900' : 'gray.700' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('video-upload')?.click()}
                minH="120px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.videoFile || formData.videoPreview ? (
                  <VStack gap={3}>
                    <video
                      src={formData.videoPreview}
                      style={{ maxHeight: '100px', borderRadius: '8px' }}
                      controls
                    />
                    <Text color="purple.600" fontWeight="medium">
                      {formData.videoFile ? formData.videoFile.name : 'Current video file'}
                    </Text>
                    {formData.videoFile && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVideoRemove();
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </VStack>
                ) : (
                  <VStack gap={2}>
                    <IconWrapper icon={FiUpload} size={24} color="gray.400" />
                    <Text color="gray.500">Click to upload new video</Text>
                    <Text fontSize="sm" color="gray.400">Current video will be replaced</Text>
                  </VStack>
                )}
                <Input
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleVideoSelect(file);
                  }}
                  display="none"
                />
              </Box>
            </FormControl>

            {/* Subtitles Upload */}
            <FormControl>
              <FormLabel color={labelColor} fontWeight="bold">
                Subtitles (Optional)
              </FormLabel>
              <Box
                border="2px dashed"
                borderColor={formData.subtitleFiles.length > 0 ? 'purple.300' : borderColor}
                borderRadius="lg"
                p={6}
                textAlign="center"
                bg={formData.subtitleFiles.length > 0 ? 'purple.50' : inputBg}
                _dark={{ bg: formData.subtitleFiles.length > 0 ? 'purple.900' : 'gray.700' }}
                cursor="pointer"
                _hover={{ borderColor: 'purple.400' }}
                transition="all 0.2s"
                onClick={() => document.getElementById('subtitle-upload')?.click()}
                minH="100px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {formData.subtitleFiles.length > 0 ? (
                  <VStack gap={3}>
                    {formData.subtitleFiles.map((subtitle, index) => (
                      <HStack key={index} gap={2}>
                        <Text color="purple.600" fontWeight="medium" fontSize="sm">
                          {(subtitle as any).url ? 
                            `Existing subtitle ${index + 1}` : 
                            subtitle.file.name
                          } ({subtitle.language})
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="red"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSubtitleRemove(index);
                          }}
                        >
                          <IconWrapper icon={FiX} size={12} />
                        </Button>
                      </HStack>
                    ))}
                    <Text fontSize="xs" color="gray.500">
                      Click to add more subtitles
                    </Text>
                  </VStack>
                ) : (
                  <VStack gap={2}>
                    <IconWrapper icon={FiFileText} size={20} color="gray.400" />
                    <Text color="gray.500" fontSize="sm">Click to upload subtitles</Text>
                    <Text fontSize="xs" color="gray.400">.srt or .vtt files</Text>
                  </VStack>
                )}
                <Input
                  id="subtitle-upload"
                  type="file"
                  accept=".srt,.vtt,text/*"
                  multiple
                  onChange={handleSubtitleSelect}
                  display="none"
                />
              </Box>
            </FormControl>
          </VStack>

          {/* Progress Bar */}
          {uploading && (
            <VStack gap={2} mt={6}>
              <Text fontSize="sm" color={labelColor}>
                Updating... {Math.round(uploadProgress)}%
              </Text>
              <Progress
                value={uploadProgress}
                size="sm"
                colorScheme="purple"
                w="100%"
                borderRadius="full"
              />
            </VStack>
          )}

          {/* Action Buttons */}
          <HStack gap={4} justify="flex-end" pt={4}>
            <Button
              variant="outline"
              onClick={() => navigate(`/content/${id}`)}
              isDisabled={uploading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSubmit}
              isLoading={uploading}
              loadingText="Updating..."
              isDisabled={!formData.title.trim() || !formData.description.trim() || formData.genres.length === 0}
            >
              Update Movie
            </Button>
          </HStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default EditMovie;
